import React, { ReactNode } from 'react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  info?: React.ErrorInfo | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // You can log the error to an error reporting service here
    // For debugging, we also set the info in state so the UI can show it.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error caught by ErrorBoundary:', error, info);
    this.setState({ error, info });
    // Try to report to server-side function for later inspection (if configured)
    try {
      const reportUrl = (import.meta as any).env?.VITE_CLIENT_ERROR_REPORT_URL;
      if (reportUrl) {
        fetch(reportUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: error?.message,
            componentStack: info?.componentStack,
            extra: {
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
              url: typeof window !== 'undefined' ? window.location.href : null
            }
          })
        }).catch(() => {});
      }
    } catch (e) {
      // ignore reporting errors
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
          <div className="max-w-3xl bg-white p-6 rounded shadow">
            <h2 className="text-xl font-bold mb-4 text-red-600">Se produjo un error inesperado</h2>
            <p className="mb-4 text-sm text-gray-700">He capturado el error para ayudarte a diagnosticarlo.</p>
            <pre className="text-xs whitespace-pre-wrap bg-gray-100 p-3 rounded max-h-96 overflow-auto">{String(this.state.error && this.state.error.message)}</pre>
            {this.state.info && (
              <details className="mt-4 text-xs">
                <summary className="cursor-pointer underline">Mostrar stack trace</summary>
                <pre className="whitespace-pre-wrap mt-2 bg-gray-100 p-3 rounded max-h-96 overflow-auto">{String(this.state.info.componentStack)}</pre>
              </details>
            )}
            <div className="mt-4">
              <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-4 py-2 rounded">Recargar</button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}
